import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Starfield } from "./Starfield";
import { Astrolabe } from "./Astrolabe";
import { Sparkle } from "./Mark";
import { NavLink } from "./Nav";
import { HERO, mailto } from "../../content";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.3, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Emblem = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const b = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - b.left) / b.width - 0.5);
    my.set((e.clientY - b.top) / b.height - 0.5);
  };

  return (
    <motion.div
      data-testid="hero-emblem"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ opacity: 0, scale: 0.86, filter: "blur(10px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      transition={{ duration: 2, delay: 0.2, ease }}
      className="relative aspect-square w-[78vw] max-w-[420px] lg:max-w-none lg:w-full mx-auto [perspective:1200px]"
    >
      <Astrolabe className="absolute -inset-[14%] w-[128%] h-[128%] text-[#B59A5B]/35" />
      <div className="absolute inset-[8%] rounded-full halo" />
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="absolute inset-0 [transform-style:preserve-3d]">
        <img
          src="/brand/emblem.jpg"
          alt="10THCELESTIAL emblem: an interlocking crescent moon and radiant sun within astronomical rings"
          className="absolute inset-0 w-full h-full object-cover rounded-full emblem-blend"
          draggable="false"
        />
        <Sparkle className="absolute top-[6%] right-[10%] w-4 h-4 text-[#E2CC92] twinkle [transform:translateZ(60px)]" />
        <Sparkle className="absolute bottom-[12%] left-[4%] w-3 h-3 text-[#E2CC92] twinkle [animation-delay:1.4s] [transform:translateZ(90px)]" />
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const yArt = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} id="home" tabIndex={-1} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden outline-none">
      <div className="absolute inset-0 hero-sky" />
      <Starfield />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-28 sm:pt-36 lg:pt-28 pb-20 lg:pb-16 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center min-h-[100svh]">
        <motion.div style={{ y: yText }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease }}
            className="font-caps text-[11px] sm:text-xs tracking-[0.35em] uppercase text-[#C9AD6A] flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[#B59A5B]/70" /> {HERO.overline}
          </motion.p>
          <h1 data-testid="hero-title" className="mt-7 font-display font-light text-[#FAF6ED] leading-[0.98] tracking-[-0.01em] text-[13vw] sm:text-7xl hero-h1">
            <MaskLine delay={0.35}>{HERO.lines[0]}</MaskLine>
            <MaskLine delay={0.55} className="italic pl-[0.6em] sm:pl-[1.2em]">
              <span className="gold-shimmer">{HERO.lines[1]}</span>
            </MaskLine>
          </h1>
          <motion.p
            data-testid="hero-intro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.05, ease }}
            className="mt-9 max-w-xl text-base sm:text-lg leading-relaxed text-[#FAF6ED]/85 font-body"
          >
            {HERO.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.25, ease }}
            className="mt-11 flex flex-wrap items-center gap-4 sm:gap-6"
          >
            <a href={mailto("Consultation inquiry")} data-testid="hero-cta-contact" className="btn-gold group">
              Contact Us <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <NavLink
              id="services"
              testid="hero-cta-services"
              className="font-caps text-xs tracking-[0.25em] uppercase text-[#FAF6ED]/90 hover:text-[#E2CC92] transition-colors inline-flex items-center gap-2"
              label={<>Explore Services <ArrowDown className="w-3.5 h-3.5" /></>}
            />
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yArt }} className="lg:col-span-5 order-1 lg:order-2">
          <Emblem />
        </motion.div>
      </div>
      <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-b from-transparent to-[#121A23] pointer-events-none" />
    </section>
  );
};
